import React, { useState } from 'react';
import { LiveKitRoom } from '@livekit/components-react';
import '@livekit/components-styles';
import './theme.css';
import './room.css';
import './join.css';
import RoomContent from './RoomContent.jsx';

const brand = {
  name: 'ShockChat',
  darkGrey: '#2a2d32',
  steelGrey: '#4a5568',
  steelLight: '#718096',
  darkBlue: '#1a365d',
  blueHover: '#2c5282',
  blueLight: '#3182ce',
  primary: '#3182ce',
  secondary: '#718096',
  accent: '#e2e8f0',
  dark: '#1a365d',
  light: '#f7fafc',
  windowBg: '#2a2d32',
  cardBg: '#363b44',
  textPrimary: '#f7fafc',
  textSecondary: '#e2e8f0',
  textMuted: '#a0aec0'
};

export default function App() {
  const [room, setRoom] = useState('');
  const [name, setName] = useState('');
  const [isHost, setIsHost] = useState(false);
  const [adminPass, setAdminPass] = useState('');
  const [token, setToken] = useState('');
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [devicesChecked, setDevicesChecked] = useState(false);
  const [availableDevices, setAvailableDevices] = useState({ audio: false, video: false });
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [roomNotice, setRoomNotice] = useState('');

  const apiUrl = import.meta.env.VITE_API_URL;
  const lkUrl = import.meta.env.VITE_LK_URL;

  async function checkDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasAudio = devices.some(device => device.kind === 'audioinput');
      const hasVideo = devices.some(device => device.kind === 'videoinput');

      setAvailableDevices({ audio: hasAudio, video: hasVideo });
      setDevicesChecked(true);

      if (!hasAudio) setAudioEnabled(false);
      if (!hasVideo) setVideoEnabled(false);

      return { audio: hasAudio, video: hasVideo };
    } catch (error) {
      console.warn('Device enumeration failed:', error);
      setAvailableDevices({ audio: false, video: false });
      setAudioEnabled(false);
      setVideoEnabled(false);
      setDevicesChecked(true);
      return { audio: false, video: false };
    }
  }

  async function join() {
    setError('');

    if (!room.trim() || !name.trim()) {
      setError('Enter both a room name and your display name.');
      return;
    }
    if (isHost && !adminPass) {
      setError('Host password is required to create a room.');
      return;
    }

    setJoining(true);
    try {
      if (!devicesChecked) await checkDevices();

      const payload = { room: room.trim(), identity: name.trim(), create: isHost };
      if (isHost) payload.hostPassword = adminPass;

      const res = await fetch(`${apiUrl}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      let data = {};
      try { data = await res.json(); } catch { /* non-JSON error body */ }

      if (!res.ok) {
        setError(data.error || `Could not join (server said ${res.status}). Please try again.`);
        return;
      }
      if (isHost && !data.roomCreate) {
        setError('Host password was not accepted.');
        return;
      }

      setToken(data.token);
      try {
        localStorage.setItem('shockchat_last', JSON.stringify({ room: room.trim(), name: name.trim() }));
        localStorage.setItem('shockchat_room', room.trim());
      } catch {}
    } catch (err) {
      console.error('Join failed:', err);
      setError('Could not reach the server. Check your connection and try again.');
    } finally {
      setJoining(false);
    }
  }

  if (token) {
    return (
      <div className="room-shell">
        <LiveKitRoom
          serverUrl={lkUrl}
          token={token}
          connect
          video={videoEnabled && availableDevices.video}
          audio={audioEnabled && availableDevices.audio}
          options={{
            publishDefaults: {
              // VP9 + simulcast: strong links get a sharp layer, weak links
              // fall back automatically instead of everyone sharing one
              // low-bitrate VP8 stream.
              videoCodec: 'vp9',
              screenShareCodec: 'vp9',
              simulcast: true,
              videoSimulcastLayers: [
                { width: 320, height: 180, encoding: { maxBitrate: 150_000, maxFramerate: 15 } },
                { width: 640, height: 360, encoding: { maxBitrate: 500_000, maxFramerate: 25 } },
                { width: 1280, height: 720, encoding: { maxBitrate: 1_700_000, maxFramerate: 30 } }
              ]
            },
            adaptiveStream: true,
            dynacast: true
          }}
          onDisconnected={() => {
            setToken('');
          }}
          onError={(error) => {
            console.error('LiveKit error:', error);
            if (error.message?.includes('NotFoundError') || error.message?.includes('device not found')) {
              setRoomNotice('Camera or microphone not found. You are still in the room — use the controls below to enable media.');
            }
          }}
        >
          <RoomContent />
        </LiveKitRoom>
      </div>
    );
  }

  return (
    <div className="join-shell">
      <main className="join-card">
        <h1 className="join-brand">{brand.name}</h1>
        <p className="join-tagline">Private video meetings. No account, no install.</p>

        <form
          className="join-fields"
          onSubmit={(e) => { e.preventDefault(); join(); }}
          noValidate
        >
          <div className="field">
            <label htmlFor="room-input">Room name</label>
            <input
              id="room-input" name="room" type="text" autoComplete="off"
              placeholder="e.g. Austin"
              value={room}
              aria-invalid={Boolean(error) && !room.trim()}
              onChange={e => { setRoom(e.target.value); if (error) setError(''); }}
            />
          </div>

          <div className="field">
            <label htmlFor="name-input">Your display name</label>
            <input
              id="name-input" name="displayName" type="text" autoComplete="nickname"
              placeholder="How others will see you"
              value={name}
              aria-invalid={Boolean(error) && !name.trim()}
              onChange={e => { setName(e.target.value); if (error) setError(''); }}
            />
          </div>

          <div className="host-panel">
            <label className="checkline" htmlFor="host-checkbox">
              <input
                id="host-checkbox" name="isHost" type="checkbox"
                checked={isHost}
                onChange={e => { setIsHost(e.target.checked); if (error) setError(''); }}
              />
              I am creating a new room (host)
            </label>

            {isHost && (
              <div className="field">
                <label htmlFor="admin-password">Host password</label>
                <input
                  id="admin-password" name="adminPassword" type="password"
                  autoComplete="current-password"
                  placeholder="Required to create a room"
                  value={adminPass}
                  onChange={e => { setAdminPass(e.target.value); if (error) setError(''); }}
                />
              </div>
            )}

            <p className="host-hint">
              Joining a room someone else started? Leave this unchecked — no password needed.
            </p>
          </div>

          {!devicesChecked ? (
            <button type="button" className="link-btn" onClick={checkDevices}>
              Check camera and microphone
            </button>
          ) : (
            <div className="device-row">
              <label className="checkline">
                <input
                  type="checkbox" checked={videoEnabled}
                  disabled={!availableDevices.video}
                  onChange={e => setVideoEnabled(e.target.checked)}
                />
                Camera{!availableDevices.video && ' (none found)'}
              </label>
              <label className="checkline">
                <input
                  type="checkbox" checked={audioEnabled}
                  disabled={!availableDevices.audio}
                  onChange={e => setAudioEnabled(e.target.checked)}
                />
                Microphone{!availableDevices.audio && ' (none found)'}
              </label>
            </div>
          )}

          {error && (
            <div className="form-msg" data-tone="error" role="alert" aria-live="assertive">
              <span aria-hidden="true">⚠</span><span>{error}</span>
            </div>
          )}

          <button type="submit" className="join-btn" disabled={joining}>
            {joining && <span className="spinner" aria-hidden="true" />}
            {joining ? 'Joining…' : 'Join room'}
          </button>
        </form>

        <p className="join-foot">A host must start the room before guests can join.</p>
      </main>
    </div>
  );
}
